import React, { ReactElement } from 'react';
import { Component, HBox, PuzzleButton, } from "puzzle";
import {
  extendDataItem,
  filterBy,
  mapTree,
  TreeList,
  TreeListCellProps,
  TreeListRowProps,
  TreeListSelectionCell,
  TreeListSelectionChangeEvent
} from '@progress/kendo-react-treelist';
import { DATA_ITEM, TREE_LIST_ITEM } from "./Types";
import { FilterDescriptor } from '@progress/kendo-data-query';
import { Keys } from "@progress/kendo-react-common";
import { TreeListSelectionCellProps } from "@progress/kendo-react-treelist/dist/npm/cells/TreeListSelectionCell";
import { removeUnnecessaryAttributes, setTableCaption } from "../../../util/Util";
import _ from "lodash";
import classNames from "classnames";

export type PROPS = {
  activeTabLanguages: boolean,
  data: DATA_ITEM[],
  expanded: (string | number)[],
  filter: FilterDescriptor[],
  onExpandChange: Function,
  selectionInfo: {},
  setSelected: Function,
  showMoreFiles: Function
};

export type STATE = {}

class CancelSelectedOnlySubmissionSection extends Component<PROPS, STATE> {
  treeListRef: any = React.createRef();
  static defaultProps = {
    filter: [],
    expanded: []
  };

  columns = [
    {
      field: 'selected',
      headerCell: () => <div className='pd-indetermintate-disabled-checkbox' tabIndex={0}>
        <span className={'sr-only'}>{this.translate('SRL-fileSelection')}</span>
      </div>,
      width: 40,
      cell: (props: TreeListSelectionCellProps) => this.renderSelectionColumn(props)
    },
    {
      field: 'name',
      title: this.translate('columnHeader.source')
    }
  ];

  /**
   * @inheritDoc
   */
  moduleDidMount = () => {
    setTableCaption(this.treeListRef.element, this.translate('SRL-table-caption'));
    removeUnnecessaryAttributes(this.treeListRef.element);
  };

  /**
   * @inheritDoc
   */
  componentDidUpdate = (prevProps: PROPS, prevState: STATE) => {
    if (!(_.isEqual(this.props, prevProps) && _.isEqual(this.state, prevState))) {
      removeUnnecessaryAttributes(this.treeListRef.element);
    }
  };

  /**
   * Generate aria label for checkboxes
   * @param dataItem
   */
  getCheckboxLabel = (dataItem: TREE_LIST_ITEM): string => {
    let ariaLabel = '';
    const { type, childName, name } = dataItem;
    const batchesCount = dataItem.subItems?.length;
    const localizationBatch = this.translate(childName ? childName : '', { length: batchesCount });
    const filesCount = type !== 'job' ? this.getTotalFilesCount(dataItem) : dataItem.filesCount;
    const localizationFiles = this.translate('files', { length: filesCount });
    switch (type) {
      case 'firstLevel':
        ariaLabel = `${name} ${batchesCount ? localizationBatch : ''}`;
        break;
      case 'job':
        ariaLabel = `${name} ${filesCount ? localizationFiles : ''}`;
        break;
      case 'file':
        ariaLabel = `${name}`;
        break;
      default :
        ariaLabel = '';
        break;
    }
    return ariaLabel
  }
  /**
   * Override and add all attributes to td and input elements for selection column
   * @param props
   */
  renderSelectionColumn = (props: TreeListSelectionCellProps): any => {
    const checkbox = TreeListSelectionCell(props);

    const children = React.Children.map(checkbox?.props.children, child => {
      if (child.type === 'label') {
        const label = this.getCheckboxLabel(props.dataItem);
        return React.cloneElement(child, {
          ...child.props,
          className: 'sr-only',
          children: label
        });
      }
      return child;
    });

    let element = checkbox ? React.cloneElement(checkbox, { ...checkbox?.props, children }) : <></>

    return element;
  }

  /**
   * Calculates total files count for topLevel dataItem in tree
   * @param dataItem
   */
  getTotalFilesCount = (dataItem: TREE_LIST_ITEM): number => {
    const jobs = dataItem.subItems;
    let returnValue = 0;
    if (jobs?.length) {
      returnValue = jobs.reduce((totalFilesCount: number, job) => job.filesCount + totalFilesCount, 0);
    }
    return returnValue;
  };

  /**
   * Returns true if passed dataItem is selected, or false otherwise
   */
  isItemSelected = (item: DATA_ITEM): boolean => {
    const { selectionInfo } = this.props;
    const { type } = item;
    let jobInfo;
    let isSelected = false;
    switch (type) {
      case 'firstLevel':
        isSelected = item.subItems.every((subItem) => {
          jobInfo = selectionInfo[(subItem.id as string)];
          return jobInfo.jobSelected;
        });
        break;
      case 'job':
        jobInfo = selectionInfo[(item.id as string)];
        isSelected = jobInfo.jobSelected;
        break;
      case 'file':
        jobInfo = selectionInfo[(item.parentId as string)];
        isSelected = jobInfo.jobSelected || jobInfo.targetsSelected.includes(item.id);
        break;
    }
    return isSelected;
  };

  /**
   * Cell renderer of TreeList
   * @param defaultCell
   * @param props
   */
  onCellRender = (defaultCell: ReactElement | null, props: TreeListCellProps): ReactElement<HTMLTableCellElement> | null => {
    const { activeTabLanguages } = this.props;
    const { dataItem, field } = props;
    const { type, name, expanded, childName, subItems } = dataItem;
    let cell = defaultCell as ReactElement<HTMLTableCellElement>;
    const treeLevel = `${this.translate('SRL-level')} ${props.level?.length}`;
    switch (field) {
      case 'name':
        let cellClassName = 'tp-text-semibold';
        let emptySpans = null;
        let nameRowAriaLabelValue = activeTabLanguages ? this.translate('language') + ' ' + dataItem.name : this.translate('batchName') + ' ' + dataItem.name;
        let childrenLocalisation = this.translate(childName ? childName : '', { length: subItems?.length });
        if (type === 'job') {
          nameRowAriaLabelValue = !activeTabLanguages ? this.translate('language') + ' ' + dataItem.name : this.translate('batchName') + ' ' + dataItem.name;
          cellClassName = 'tp-text-semibold';
          emptySpans = <span aria-hidden="true" className='k-icon k-i-none tp-treelist-separator-icon' />;
        }
        if (type === 'file') {
          nameRowAriaLabelValue = this.translate('documentName') + ' ' + dataItem.name;
          cellClassName = '';
          emptySpans =
            <>
              <span aria-hidden="true" className='k-icon k-i-none tp-treelist-separator-icon' />
              <span aria-hidden="true" className='k-icon k-i-none tp-treelist-separator-icon' />
            </>
        }
        const children = (<HBox alignItemsCenter className={'tp-pointer-events-none'}>
          {emptySpans}
          {dataItem.type !== "file" &&
            <span
              aria-hidden="true"
              className={`tp-icon pd-toggle-row-icon ${expanded ? 'tp-remove' : 'tp-add'} tp-color-accent tp-font-size-xl tp-pointer-events-none`}
            />
          }
          {this.renderSelectionCheckbox(dataItem, name)} </HBox>)
        cell = defaultCell ? React.cloneElement(defaultCell, {
          ...defaultCell?.props,
          className: cellClassName,
          'aria-label': `${treeLevel} ${nameRowAriaLabelValue} ${childrenLocalisation}`,
          title: dataItem.name
        }, children) : <td />;
        break;
    }
    return cell
  };

  /**
   * Custom rendering for rows
   * @param row
   * @param props
   */
  onRowRender = (row: ReactElement<HTMLTableRowElement>, props: TreeListRowProps): ReactElement<HTMLTableCellElement> => {
    const { dataItem } = props;
    let isExpanded = this.props.expanded.includes(dataItem.id);
    const rowCls = classNames(
      `tp-${dataItem.type}-row ${row.props.className}`, {
      'tp-row-expanded': isExpanded,
      'k-state-selected': dataItem.selected,
      'tp-pointable': dataItem.type !== 'file'
    });
    let ariaExpanded = dataItem.type !== 'file' ? isExpanded ? 'true' : 'false' : null;
    const returnValue = (
      <>
        <row.type
          {...row.props}
          aria-expanded={ariaExpanded}
          tabIndex={0}
          className={rowCls}
          onKeyUp={this.rowKeyUpHandler.bind(this, { dataItem: dataItem })}
        >
          {row.props.children}
        </row.type>
        {
          dataItem.hasMoreFiles &&
          <tr
            className={'k-master-row tp-show-more-files-row'}
            tabIndex={0}
          >
            <td colSpan={3}>
              <span className='k-icon k-i-none tp-treelist-separator-icon' />
              <span className='k-icon k-i-none tp-treelist-separator-icon' />
              <PuzzleButton
                className={'tp-show-more-files-btn tp-font-size-sm'}
                size={'small'}
                themeColor={'primary'}
                fillMode="flat"
                onClick={this.props.showMoreFiles.bind(null, dataItem)}
              >
                {this.translate('showMoreFiles')}
              </PuzzleButton>
            </td>
          </tr>
        }
      </>
    );

    return returnValue
  };

  /**
   * Handles selection change for all dataItems
   * passes dataItem and selected state of item to parent component's selection handler
   */
  onSelectionChange = (event: TreeListSelectionChangeEvent) => {
    this.props.setSelected(event.dataItem, !event.dataItem.selected)
  };

  /**
   * Renders checkbox that indicates selected state of an item
   * @param dataItem
   * @param label
   */
  renderSelectionCheckbox = (dataItem: TREE_LIST_ITEM, label: any) => {
    return <HBox htmlTag={'span'} alignItemsCenter gap={1} className={'pd-label-text tp-pointer-events-none'}>{this.renderLabel(dataItem, label)}</HBox>
  }

  /**
   * Renders label for checkbox
   * @param dataItem
   * @param label
   */
  renderLabel = (dataItem: TREE_LIST_ITEM, label: any): JSX.Element => {
    const { childName, type } = dataItem;
    const batchesCount = dataItem.subItems?.length;
    const localizationBatch = <span
      className={'pd-files-label tp-font-size-sm tp-color-light tp-text-regular'}>{this.translate(childName ? childName : '', { length: batchesCount })}</span>;
    const filesCount = type !== 'job' ? this.getTotalFilesCount(dataItem) : dataItem.filesCount;
    const localizationFiles = <span
      className={'pd-files-label tp-font-size-sm tp-color-light tp-text-regular'}>{this.translate('files', { length: filesCount })}</span>;
    let customLabel: JSX.Element;
    switch (type) {
      case 'firstLevel':
        customLabel = <>{label} {batchesCount && <><span className={'pd-label-separator'} aria-hidden="true">|</span>{localizationBatch}</>}</>;
        break;
      case 'job':
        customLabel = <>{label} {filesCount && <><span className={'pd-label-separator'} aria-hidden="true">|</span>{localizationFiles}</>}</>
        break;
      case 'file':
        customLabel = <>{label}</>
        break;
      default :
        customLabel = <></>
        break;
    }
    return customLabel;
  }

  /**
   * Expands row on enter key
   * @param dataItem
   * @param e
   */
  rowKeyUpHandler = (dataItem: any, e: KeyboardEvent) => {
    let { keyCode } = e;
    if (Keys.enter === keyCode) {
      this.props.onExpandChange(dataItem);
    }
  };

  /**
   * First filters data, then sets selected and expanded items on render
   * @param data
   */
  updateFields = (data: DATA_ITEM[]): TREE_LIST_ITEM[] => {
    const { filter, expanded } = this.props;
    const filterText = filter?.[1]?.value;
    const filteredData = filterText ? filterBy(data, filter, 'subItems') : data;
    return mapTree(filteredData, 'subItems', (item: DATA_ITEM) =>
      extendDataItem(item, 'subItems', {
        expanded: filterText || expanded.includes(item.id),
        selected: this.isItemSelected(item)
      })
    );
  };

  renderBody() {
    const { data } = this.props;
    const treeData: TREE_LIST_ITEM[] = this.updateFields(data);
    return (
      <>
        {data &&
          <>
            <TreeList
              ref={(treeList: any) => this.treeListRef = treeList}
              tableProps={{ tabIndex: 0 }}
              rowHeight={48}
              cellRender={this.onCellRender}
              rowRender={this.onRowRender}
              className={`tp-cancel-submission-subs-list tp-no-border ${treeData.length ? '': 'tp-empty-grid'}`}
              data={this.updateFields(data)}
              expandField={'expanded'}
              onSelectionChange={this.onSelectionChange}
              subItemsField={'subItems'}
              onExpandChange={this.props.onExpandChange}
              columns={this.columns}
              onRowClick={this.props.onExpandChange}
            />
          </>
        }
      </>
    );
  }
}

export default CancelSelectedOnlySubmissionSection
